<template>
  <div class="build-pack">
    <div class="page-header">
      <h1>制作表情包</h1>
      <p class="page-desc">上传多张 GIF/PNG 图片，自动生成 BIN 格式表情包。必须包含名为 <strong>neutral</strong> 的图片（如 neutral.gif、neutral.png）。</p>
    </div>

    <el-card class="form-card">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="120px">
        <el-form-item label="表情包名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入名称" maxlength="64" show-word-limit />
        </el-form-item>
        <el-form-item label="版本号" prop="version">
          <el-input v-model="form.version" placeholder="如 1.0.0" maxlength="32" />
        </el-form-item>
        <el-form-item label="适配版型" prop="board_types">
          <el-checkbox-group v-model="form.board_types">
            <el-checkbox v-for="board in boardTypes" :key="board.id" :value="board.id">
              {{ board.name }}
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="表情图片">
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :on-change="onFileChange"
            :on-remove="onFileRemove"
            :file-list="fileList"
            accept=".gif,.png"
            multiple
            list-type="picture-card"
          >
            <template #default>
              <el-icon><Plus /></el-icon>
            </template>
            <template #file="{ file }">
              <div class="upload-file-item">
                <img v-if="file.url" :src="file.url" class="upload-file-img" alt="" />
                <el-icon v-else class="upload-file-placeholder"><Picture /></el-icon>
                <span class="upload-file-name" :title="file.name">{{ file.name }}</span>
              </div>
            </template>
          </el-upload>
          <div class="upload-tip">
            支持 GIF、PNG，可多选。文件名将作为表情名（如 happy.gif、neutral.png）。<strong>必须包含名为 neutral 的图片。</strong><br>
            <span style="color: #E6A23C;">注意：所有图片总大小不能超过 2MB（硬件分区限制）。</span><br>
            <span style="color: #909399;">尺寸建议如下尺寸（如 240×240，260×260），不要随意改分辨率，否则硬件端可能无法正常显示。</span>
          </div>
        </el-form-item>
      </el-form>

      <div class="actions">
        <el-button type="primary" native-type="button" :loading="submitting" @click="submit">开始制作</el-button>
        <el-button @click="$router.push('/packs')">返回表情管理</el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { Plus, Picture } from '@element-plus/icons-vue';
import { buildBinFromImages } from '../api/emotion';
import { getBoardTypes } from '../api/device';

export default {
  name: 'BuildPack',
  components: { Plus, Picture },
  setup() {
    const router = useRouter();
    const formRef = ref(null);
    const uploadRef = ref(null);
    const boardTypes = ref([]);
    const fileList = ref([]);
    const submitting = ref(false);

    const form = reactive({
      name: '',
      version: '1.0.0',
      board_types: []
    });

    const rules = {
      name: [{ required: true, message: '请输入表情包名称', trigger: 'blur' }],
      version: [{ required: true, message: '请输入版本号', trigger: 'blur' }]
    };

    const onFileChange = (file, list) => {
      const ext = (file.name || '').toLowerCase().split('.').pop();
      if (ext !== 'gif' && ext !== 'png') {
        ElMessage.warning('仅支持 .gif 和 .png 文件');
        const idx = list.findIndex(f => f.uid === file.uid);
        if (idx >= 0) list.splice(idx, 1);
        return;
      }
      fileList.value = list;
    };

    const onFileRemove = (file, list) => {
      fileList.value = list;
    };

    const hasNeutral = () => {
      return fileList.value.some(f => {
        const name = (f.name || '').replace(/\.[^.]*$/, '').trim().toLowerCase();
        return name === 'neutral';
      });
    };

    const submit = async () => {
      if (!formRef.value) {
        ElMessage.warning('表单未就绪，请稍后再试');
        return;
      }
      try {
        await formRef.value.validate();
      } catch {
        ElMessage.warning('请填写完整信息（表情包名称、版本号）');
        return;
      }
      if (!form.name || !form.version) {
        ElMessage.warning('请填写表情包名称和版本号');
        return;
      }
      if (!fileList.value || fileList.value.length === 0) {
        ElMessage.warning('请至少上传一张图片');
        return;
      }
      if (!hasNeutral()) {
        ElMessage.warning('必须包含名为 neutral 的图片（如 neutral.gif、neutral.png）');
        return;
      }

      // 等待所有文件的 .raw 属性就绪（一次性拖入多张时可能需要时间）
      let files = fileList.value.map(f => f.raw).filter(Boolean);
      if (files.length === 0) {
        ElMessage.warning('请选择有效的图片文件');
        return;
      }
      // 如果文件数量不匹配，等待一下再试（最多等待 1 秒）
      if (files.length < fileList.value.length) {
        await new Promise(resolve => setTimeout(resolve, 200));
        files = fileList.value.map(f => f.raw).filter(Boolean);
        if (files.length < fileList.value.length) {
          ElMessage.warning(`部分文件尚未就绪（${files.length}/${fileList.value.length}），请稍后再试`);
          return;
        }
      }

      // 检查图片总大小（硬件分区限制 2MB）
      const MAX_TOTAL_SIZE = 2 * 1024 * 1024; // 2MB
      const totalSize = files.reduce((sum, f) => sum + (f.size || 0), 0);
      if (totalSize > MAX_TOTAL_SIZE) {
        const sizeMB = (totalSize / (1024 * 1024)).toFixed(2);
        ElMessage.warning(`图片总大小 ${sizeMB}MB 超过限制（最大 2MB），请减少图片数量或压缩图片`);
        return;
      }

      submitting.value = true;
      try {
        const res = await buildBinFromImages(
          files,
          form.name,
          form.version,
          form.board_types && form.board_types.length ? form.board_types : null
        );
        if (res && res.success) {
          ElMessage.success('制作成功');
          router.push('/packs');
        } else {
          ElMessage.error(res?.message || '制作失败');
        }
      } catch (e) {
        const msg = e.response?.data?.message || e.message || '制作失败';
        ElMessage.error(msg);
      } finally {
        submitting.value = false;
      }
    };

    onMounted(async () => {
      try {
        const r = await getBoardTypes();
        if (r && r.data) boardTypes.value = r.data;
      } catch {
        // 获取版型列表失败，忽略错误
      }
    });

    return {
      formRef,
      uploadRef,
      form,
      rules,
      boardTypes,
      fileList,
      submitting,
      onFileChange,
      onFileRemove,
      submit
    };
  }
};
</script>

<style scoped>
.build-pack {
  padding: 20px;
}
.page-header {
  margin-bottom: 20px;
}
.page-header h1 {
  margin: 0 0 8px 0;
  font-size: 22px;
}
.page-desc {
  margin: 0;
  color: #606266;
  font-size: 14px;
}
.form-card {
  max-width: 720px;
}
.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}
.actions {
  margin-top: 24px;
}

.upload-file-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
}
.upload-file-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  flex: 1;
  min-height: 0;
}
.upload-file-placeholder {
  font-size: 48px;
  color: #c0c4cc;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.upload-file-name {
  width: 100%;
  padding: 4px 2px 0;
  font-size: 12px;
  color: #606266;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.2;
}
</style>
